import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { inr, dateShort, errMsg } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import StatusBadge from "@/components/StatusBadge";
import WhatsAppBroadcastModal from "@/components/WhatsAppBroadcastModal";
import { Plus, Search, TrendingUp, Cpu, MessageSquare } from "lucide-react";
import { toast } from "sonner";

const STATUSES = ["New", "Contacted", "Follow Up", "Interested", "Meeting Scheduled",
  "Proposal Sent", "Negotiation", "Won", "Lost"];
const SOURCES = ["Coaching Data", "Calling List", "Website", "WhatsApp", "LinkedIn", "Facebook", "Walk-In", "Referral", "Google Ads"];
const CUSTOMER_TYPES = ["Student / Parent", "Revision Batch", "Demo Class", "Startup Website", "E-commerce Business", "Mobile App Client", "Local SEO Audit", "SaaS Software Partner", "General Lead"];

export default function Leads() {
  const navigate = useNavigate();
  const [data, setData] = useState({ items: [], total: 0, page: 1, page_size: 20 });
  const [filters, setFilters] = useState({ search: "", status: "", source: "" });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [broadcastOpen, setBroadcastOpen] = useState(false);

  function load() {
    setLoading(true);
    const params = { page, page_size: 20 };
    if (filters.search) params.search = filters.search;
    if (filters.status) params.status = filters.status;
    if (filters.source) params.source = filters.source;
    api.get("/leads", { params })
      .then((r) => {
        setData(r.data);
        setSelectedIds(new Set());
      })
      .catch((e) => toast.error(errMsg(e)))
      .finally(() => setLoading(false));
  }
  useEffect(load, [page, filters.status, filters.source]);

  const totalPages = Math.max(1, Math.ceil(data.total / 20));

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelectedIds((prev) => {
      if (prev.size === data.items.length) {
        return new Set();
      } else {
        return new Set(data.items.map((item) => item.id));
      }
    });
  };

  const deleteSelected = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Are you sure you want to delete ${selectedIds.size} selected lead(s)?`)) return;
    try {
      await api.post("/leads/delete-multiple", Array.from(selectedIds));
      toast.success("Selected leads deleted successfully.");
      setSelectedIds(new Set());
      load();
    } catch (e) {
      toast.error(errMsg(e, "Failed to delete selected leads"));
    }
  };

  const deleteAll = async () => {
    if (!window.confirm("Are you sure you want to delete ALL leads in the database? This will also clear all calls, tasks, and chats linked to them.")) return;
    try {
      await api.post("/leads/delete-all");
      toast.success("All leads deleted successfully.");
      setSelectedIds(new Set());
      load();
    } catch (e) {
      toast.error(errMsg(e, "Failed to delete all leads"));
    }
  };

  return (
    <div data-testid="leads-page" className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-serif text-3xl">Leads</h1>
          <p className="text-sm text-slate-600">{data.total} leads in pipeline</p>
        </div>
        <div className="flex items-center gap-2">
          {selectedIds.size > 0 && (
            <>
              <Button
                className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5 shadow-sm font-medium"
                onClick={() => setBroadcastOpen(true)}
              >
                <MessageSquare className="h-4 w-4" />
                WhatsApp Broadcast ({selectedIds.size})
              </Button>
              <Button variant="destructive" className="bg-rose-600 hover:bg-rose-700 text-white" onClick={deleteSelected}>
                Delete Selected ({selectedIds.size})
              </Button>
            </>
          )}
          <Button variant="outline" className="text-rose-600 border-rose-200 hover:bg-rose-50 hover:text-rose-700" onClick={deleteAll}>
            Delete All
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="border-indigo-200 dark:border-indigo-500/40 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-50 dark:hover:bg-indigo-950/60">
                <Cpu className="h-4 w-4 mr-1.5" />
                Automation
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-white dark:bg-slate-900 border-indigo-100 dark:border-white/10">
              <DropdownMenuItem onClick={() => navigate("/automation/builder?template=followup")}>
                Create Follow-up Workflow
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/automation/builder?template=auto_assign")}>
                Auto Assign Workflow
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/automation/builder?template=whatsapp")}>
                Auto WhatsApp Workflow
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/automation/builder?template=reminder")}>
                Auto Reminder Workflow
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-lead-btn" className="bg-indigo-600 hover:bg-indigo-700 text-white">
                <Plus className="h-4 w-4 mr-1.5" /> New Lead
              </Button>
            </DialogTrigger>
            <NewLeadDialog onSaved={() => { setOpen(false); load(); }} />
          </Dialog>
        </div>
      </div>

      <Card className="p-4 border-indigo-100 dark:border-white/10 bg-white dark:bg-slate-900/80">
        <div className="grid sm:grid-cols-4 gap-3">
          <div className="sm:col-span-2 relative">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
            <Input
              data-testid="leads-search"
              placeholder="Search name, phone, email or company"
              className="pl-9"
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              onKeyDown={(e) => e.key === "Enter" && (setPage(1), load())}
            />
          </div>
          <Select
            value={filters.status || "all"}
            onValueChange={(v) => { setPage(1); setFilters({ ...filters, status: v === "all" ? "" : v }); }}
          >
            <SelectTrigger data-testid="leads-filter-status"><SelectValue placeholder="All status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All status</SelectItem>
              {STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select
            value={filters.source || "all"}
            onValueChange={(v) => { setPage(1); setFilters({ ...filters, source: v === "all" ? "" : v }); }}
          >
            <SelectTrigger data-testid="leads-filter-source"><SelectValue placeholder="All sources" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All sources</SelectItem>
              {SOURCES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </Card>

      <Card className="border-indigo-100 dark:border-white/10 bg-white dark:bg-slate-900/80 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="leads-table">
            <thead className="bg-indigo-50/60 dark:bg-white/[0.03] text-slate-700 dark:text-slate-200">
              <tr>
                <th className="px-4 py-3 w-10 text-center">
                  <input
                    type="checkbox"
                    checked={data.items.length > 0 && selectedIds.size === data.items.length}
                    onChange={toggleSelectAll}
                    className="rounded border-slate-300 dark:border-white/20 text-indigo-600 focus:ring-indigo-500 cursor-pointer bg-transparent"
                  />
                </th>
                <th className="text-left px-4 py-3 font-semibold">Name</th>
                <th className="text-left px-4 py-3 font-semibold">City</th>
                <th className="text-left px-4 py-3 font-semibold">Source</th>
                <th className="text-left px-4 py-3 font-semibold">Type</th>
                <th className="text-right px-4 py-3 font-semibold">Budget</th>
                <th className="text-left px-4 py-3 font-semibold">Status</th>
                <th className="text-left px-4 py-3 font-semibold flex items-center gap-1">
                  <TrendingUp className="h-3.5 w-3.5 text-indigo-600 dark:text-indigo-400" /> AI Score
                </th>
                <th className="text-left px-4 py-3 font-semibold">Created</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9} className="p-6 text-center text-slate-400">Loading…</td></tr>
              ) : data.items.length === 0 ? (
                <tr><td colSpan={9} className="p-6 text-center text-slate-400">No leads</td></tr>
              ) : data.items.map((l) => (
                <tr key={l.id} className="border-t border-indigo-50 dark:border-white/5 hover:bg-indigo-50/30 dark:hover:bg-slate-800/40 transition-colors">
                  <td className="px-4 py-3 text-center">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(l.id)}
                      onChange={() => toggleSelect(l.id)}
                      className="rounded border-slate-300 dark:border-white/20 text-indigo-600 focus:ring-indigo-500 cursor-pointer bg-transparent"
                    />
                  </td>
                  <td className="px-4 py-3">
                    <Link to={`/leads/${l.id}`} data-testid={`lead-row-${l.id}`} className="font-medium text-slate-900 dark:text-slate-100 hover:text-indigo-600 dark:hover:text-indigo-400">
                      {l.name}
                    </Link>
                    <div className="text-xs text-slate-500 dark:text-slate-400">{l.phone}</div>
                  </td>
                  <td className="px-4 py-3 text-slate-700 dark:text-slate-300">{l.city || "—"}</td>
                  <td className="px-4 py-3 text-slate-700 dark:text-slate-300">{l.source || "—"}</td>
                  <td className="px-4 py-3 text-slate-700 dark:text-slate-300">{l.customer_type || "—"}</td>
                  <td className="px-4 py-3 text-right font-medium text-slate-900 dark:text-slate-100">{inr(l.budget)}</td>
                  <td className="px-4 py-3"><StatusBadge value={l.status} /></td>
                  <td className="px-4 py-3">
                    {l.lead_score != null ? (
                      <div className="flex flex-col gap-0.5">
                        <span className={`inline-flex items-center text-[11px] font-bold px-2 py-0.5 rounded-full border w-fit ${
                          l.lead_score >= 75 ? "bg-emerald-50 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800/60" :
                          l.lead_score >= 50 ? "bg-indigo-50 dark:bg-indigo-950/60 text-indigo-700 dark:text-indigo-300 border-indigo-200 dark:border-indigo-800/60" :
                          "bg-rose-50 dark:bg-rose-950/60 text-rose-600 dark:text-rose-300 border-rose-200 dark:border-rose-800/60"
                        }`}>{l.lead_score}%</span>
                        {l.intent && <span className="text-[10px] text-slate-400 truncate max-w-[100px]">{l.intent}</span>}
                      </div>
                    ) : (
                      <span className="text-xs text-slate-400">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-500 dark:text-slate-400 whitespace-nowrap">{dateShort(l.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between p-3 border-t border-indigo-100 dark:border-white/5 bg-indigo-50/30 dark:bg-slate-900/40 text-sm text-slate-600 dark:text-slate-300">
          <div>Page {data.page} of {totalPages}</div>
          <div className="flex gap-2">
            <Button data-testid="leads-prev" size="sm" variant="outline" disabled={page <= 1}
              onClick={() => setPage(p => Math.max(1, p - 1))}>Prev</Button>
            <Button data-testid="leads-next" size="sm" variant="outline" disabled={page >= totalPages}
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}>Next</Button>
          </div>
        </div>
      </Card>

      <WhatsAppBroadcastModal
        open={broadcastOpen}
        onOpenChange={setBroadcastOpen}
        preselectedLeadIds={Array.from(selectedIds)}
        onSuccess={() => {
          setSelectedIds(new Set());
          load();
        }}
      />
    </div>
  );
}

function NewLeadDialog({ onSaved }) {
  const [form, setForm] = useState({
    name: "", phone: "", email: "", city: "", company: "",
    source: "Calling List", status: "New", customer_type: "Student / Parent",
    budget: 0, notes: "",
  });
  const [saving, setSaving] = useState(false);

  async function save() {
    setSaving(true);
    try {
      const payload = {
        ...form,
        budget: Number(form.budget) || 0,
        email: form.email?.trim() || null,
        phone: form.phone?.trim() || null,
        city: form.city?.trim() || null,
        company: form.company?.trim() || null,
        notes: form.notes?.trim() || null,
      };
      await api.post("/leads", payload);
      toast.success("Lead created");
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <DialogContent className="max-w-xl">
      <DialogHeader>
        <DialogTitle className="font-serif">Add New Lead</DialogTitle>
      </DialogHeader>
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Name *" testid="new-lead-name" value={form.name}
          onChange={(v) => setForm({ ...form, name: v })} />
        <Field label="Phone *" testid="new-lead-phone" value={form.phone}
          onChange={(v) => setForm({ ...form, phone: v })} />
        <Field label="Email (Optional)" testid="new-lead-email" value={form.email}
          onChange={(v) => setForm({ ...form, email: v })} />
        <Field label="City" testid="new-lead-city" value={form.city}
          onChange={(v) => setForm({ ...form, city: v })} />
        <Field label="School / Coaching / Company" testid="new-lead-company" value={form.company}
          onChange={(v) => setForm({ ...form, company: v })} />
        <Field label="Fee / Budget (₹)" type="number" testid="new-lead-budget" value={form.budget}
          onChange={(v) => setForm({ ...form, budget: v })} />
        <SelectField label="Source" testid="new-lead-source" value={form.source}
          options={SOURCES} onChange={(v) => setForm({ ...form, source: v })} />
        <SelectField label="Status" testid="new-lead-status" value={form.status}
          options={STATUSES} onChange={(v) => setForm({ ...form, status: v })} />
        <SelectField label="Customer Type" testid="new-lead-type" value={form.customer_type}
          options={CUSTOMER_TYPES} onChange={(v) => setForm({ ...form, customer_type: v })} />
      </div>
      <DialogFooter>
        <Button data-testid="new-lead-save" className="bg-indigo-700 hover:bg-indigo-800"
          disabled={saving || !form.name.trim()} onClick={save}>Save Lead</Button>
      </DialogFooter>
    </DialogContent>
  );
}

function Field({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <Label className="text-xs text-slate-600">{label}</Label>
      <Input data-testid={testid} type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="mt-1" />
    </div>
  );
}
function SelectField({ label, value, onChange, options, testid }) {
  return (
    <div>
      <Label className="text-xs text-slate-600">{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger data-testid={testid} className="mt-1"><SelectValue /></SelectTrigger>
        <SelectContent>{options.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
}
